import React, { useState, useEffect } from 'react';
import {
  Container,
  Card,
  Form,
  Row,
  Col,
  Button,
  Accordion,
  Badge,
  FloatingLabel,
  Alert,
  Modal,
} from 'react-bootstrap';
import {
  Send,
  Calendar,
  Bell,
  Users,
  Mail,
  MessageSquare,
  Smartphone,
  Building,
  LayoutTemplate,
  Clock,
  Award,
  Cake,
  Heart,
  TrendingUp,
  ChevronRight,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Clock4,
} from 'lucide-react';
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import '../Styling/OrgNotifications.css';
import { getNotificationData, postNotificationData, getStoredUser } from '../Api/Notification';

// Lucide icon mapping based on API response
const iconMapping = {
  'Cake': <Cake size={20} />,
  'Award': <Award size={20} />,
  'Heart': <Heart size={20} />,
  'TrendingUp': <TrendingUp size={20} />,
  'ChevronRight': <ChevronRight size={20} />,
  'Calendar': <Calendar size={20} />,
};

// Color mapping for event types
const eventColors = {
  'Birthday': '#FF9AA2',
  'Work Anniversary': '#C7CEEA',
  'Marriage Anniversary': '#FFDAC1',
  'Salary Appraisal': '#B5EAD7',
  'Promotion': '#E2F0CB',
  'Public Holiday': '#F9C5D1',
};

const OrgNotifications = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [apiData, setApiData] = useState({
    eventTypes: [],
    templates: [],
    scheduleTimings: [],
    deliveryChannels: [],
    officeMasters: [],
  });
  
  const [notificationData, setNotificationData] = useState({
    eventType: '',
    eventId: null,
    notificationTiming: '',
    scheduleId: null,
    channels: {},
    deliveryIds: [],
    offices: [],
    recipients: {},
    directEmails: [''],
    templates: {}, // Stores selected template IDs for each channel
    templateIds: [], // Array of all selected template IDs
    customDate: null,
    preferTime: '',
  });

  const [showDatePicker, setShowDatePicker] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const recipientCategories = [
    { id: 1, label: 'Self' },
    { id: 2, label: 'Team Members' },
    { id: 3, label: 'Team Lead' },
    { id: 4, label: 'Manager' },
    { id: 5, label: 'Department' },
  ];

  useEffect(() => {
    fetchNotificationData();
  }, []);

  const fetchNotificationData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getNotificationData();
      
      const initialChannels = {};
      data.deliveryChannels?.forEach(channel => {
        const channelId = channel.deliveryType?.toLowerCase();
        initialChannels[channelId] = false;
      });
      
      const initialRecipients = {};
      recipientCategories.forEach(recipient => {
        initialRecipients[recipient.id] = false;
      });
      
      const initialTemplates = {};
      data.deliveryChannels?.forEach(channel => {
        const channelId = channel.deliveryType?.toLowerCase();
        initialTemplates[channelId] = { 
          templateId: null, 
          templateTittle: '', 
          templateDescription: '', 
          templateVariables: '' 
        };
      });
      
      setApiData(data);
      setNotificationData(prev => ({
        ...prev,
        channels: initialChannels,
        recipients: initialRecipients,
        templates: initialTemplates
      }));
      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setError(error.message || "Failed to load notification data");
      setLoading(false);
    }
  };

  const getChannelOptions = () => {
    if (!apiData.deliveryChannels || apiData.deliveryChannels.length === 0) {
      return [];
    }
    
    const channelIcons = {
      'Email': <Mail size={18} />,
      'WhatsApp': <MessageSquare size={18} />,
      'SMS': <Smartphone size={18} />,
      'In-App': <Bell size={18} />,
    };
    
    return apiData.deliveryChannels.map(channel => ({
      id: channel.deliveryType?.toLowerCase(),
      label: channel.deliveryType,
      icon: channelIcons[channel.deliveryType] || <Bell size={18} />,
      deliveryId: channel.deliveryId
    }));
  };

  const getTimingOptions = () => {
    if (!apiData.scheduleTimings || apiData.scheduleTimings.length === 0) {
      return [];
    }
    
    const timingOptions = apiData.scheduleTimings.map(timing => {
      const descriptions = {
        'Beginning of the Month': 'Send on 1st of every month',
        'End of the Month': 'Send on last day of month',
        '5 Days Before Event': 'Send 5 days before the actual date',
        'Day of Event': 'Send on the exact date',
        'Custom Date': 'Select specific date'
      };
      
      const icons = {
        'Beginning of the Month': '📅',
        'End of the Month': '📆',
        '5 Days Before Event': '⏰',
        'Day of Event': '🎯',
        'Custom Date': '📌'
      };
      
      return {
        id: timing.scheduleId,
        label: timing.scheduleType,
        description: descriptions[timing.scheduleType] || timing.scheduleType,
        icon: icons[timing.scheduleType] || '📅'
      };
    });
    
    return timingOptions;
  };

  const getTemplatesByEventAndChannel = (eventType, channelId) => {
    if (!eventType || !apiData.templates || apiData.templates.length === 0) return [];
    
    const channelOption = getChannelOptions().find(c => c.id === channelId);
    if (!channelOption) return [];
    
    const eventKeywords = {
      'Birthday': ['birthday', 'bday', 'birth date', 'happy birthday'],
      'Work Anniversary': ['work anniversary', 'service anniversary', 'joining', 'years at', 'congratulations on'],
      'Marriage Anniversary': ['marriage', 'wedding', 'anniversary'],
      'Salary Appraisal': ['salary', 'appraisal', 'increment', 'raise'],
      'Promotion': ['promotion', 'promoted', 'new role', 'congratulations'],
      'Public Holiday': ['holiday', 'public holiday', 'festival', 'day off'],
      'Employee Confirmation': ['confirmation', 'probation', 'permanent', 'confirmed', 'last working day', 'termination']
    };
    
    const keywords = eventKeywords[eventType] || [eventType.toLowerCase()];
    
    return apiData.templates.filter(template => {
      const matchesChannel = template.deliveryId === channelOption.deliveryId;
      if (!matchesChannel) return false;
      
      const templateText = `${template.templateTittle || ''} ${template.templateDescription || ''}`.toLowerCase();
      const matchesEvent = keywords.some(keyword => templateText.includes(keyword.toLowerCase()));
      return matchesEvent;
    });
  };

  const handleEventTypeChange = (eventId, eventType) => {
    setNotificationData(prev => ({
      ...prev,
      eventType: eventType,
      eventId: eventId,
      templates: Object.keys(prev.templates).reduce((acc, key) => ({
        ...acc,
        [key]: { templateId: null, templateTittle: '', templateDescription: '', templateVariables: '' }
      }), {}),
      templateIds: [],
    }));
  };

  const handleChannelToggle = (channel) => {
    setNotificationData(prev => {
      const newChannels = {
        ...prev.channels,
        [channel.id]: !prev.channels[channel.id],
      };
      
      let newDeliveryIds = [...prev.deliveryIds];
      if (newChannels[channel.id]) {
        if (!newDeliveryIds.includes(channel.deliveryId)) {
          newDeliveryIds.push(channel.deliveryId);
        }
      } else {
        newDeliveryIds = newDeliveryIds.filter(id => id !== channel.deliveryId);
        const newTemplates = { ...prev.templates };
        if (newTemplates[channel.id]?.templateId) {
          const index = prev.templateIds.indexOf(newTemplates[channel.id].templateId);
          if (index > -1) {
            newTemplates[channel.id] = { 
              templateId: null, 
              templateTittle: '', 
              templateDescription: '', 
              templateVariables: '' 
            };
          }
        }
      }
      
      return { 
        ...prev, 
        channels: newChannels,
        deliveryIds: newDeliveryIds
      };
    });
  };

  const handleOfficeToggle = (officeCode) => {
    setNotificationData(prev => {
      const offices = prev.offices.includes(officeCode)
        ? prev.offices.filter(id => id !== officeCode)
        : [...prev.offices, officeCode];
      return { ...prev, offices };
    });
  };

  const handleRecipientToggle = (recipientId) => {
    setNotificationData(prev => ({
      ...prev,
      recipients: {
        ...prev.recipients,
        [recipientId]: !prev.recipients[recipientId],
      },
    }));
  };

  const handleDirectEmailChange = (index, value) => {
    const newEmails = [...notificationData.directEmails];
    newEmails[index] = value;
    setNotificationData(prev => ({ ...prev, directEmails: newEmails }));
  };

  const handleRemoveEmail = (index) => {
    if (notificationData.directEmails.length > 1) {
      const newEmails = [...notificationData.directEmails];
      newEmails.splice(index, 1);
      setNotificationData(prev => ({ ...prev, directEmails: newEmails }));
    }
  };

  // SIMPLIFIED: Just select template without preview/modal
  const handleTemplateSelect = (channelId, template) => {
    setNotificationData(prev => {
      const newTemplates = { ...prev.templates };
      let newTemplateIds = [...prev.templateIds];
      
      const oldTemplateId = newTemplates[channelId]?.templateId;
      if (oldTemplateId) {
        const index = newTemplateIds.indexOf(oldTemplateId);
        if (index > -1) {
          newTemplateIds.splice(index, 1);
        }
      }
      
      newTemplates[channelId] = {
        templateId: template.templateId,
        templateTittle: template.templateTittle,
        templateDescription: template.templateDescription,
        templateVariables: template.templateVariables,
      };
      
      if (template.templateId && !newTemplateIds.includes(template.templateId)) {
        newTemplateIds.push(template.templateId);
      }
      
      return {
        ...prev,
        templates: newTemplates,
        templateIds: newTemplateIds,
      };
    });
    
    toast.success(`✓ ${template.templateTittle} selected for ${channelId}`);
  };

  const handleTimingSelect = (timing) => {
    if (timing.label === 'Custom Date') {
      setShowDatePicker(true);
    } else {
      setNotificationData(prev => ({ 
        ...prev, 
        notificationTiming: timing.label,
        scheduleId: timing.id,
        customDate: null
      }));
    }
  };

  const handleCustomDateSelect = (date) => {
    setShowDatePicker(false);
    setNotificationData(prev => ({ 
      ...prev, 
      notificationTiming: 'Custom Date',
      scheduleId: 5,
      customDate: date
    }));
  };

  const handlePreferTimeChange = (time) => {
    setNotificationData(prev => ({ 
      ...prev, 
      preferTime: time
    }));
  };

  const timeToTicks = (timeStr) => {
    if (!timeStr) return 0;
    
    try {
      let time = timeStr.trim().toUpperCase();
      const hasAmPm = time.includes('AM') || time.includes('PM');
      let hours, minutes;
      
      if (hasAmPm) {
        const [timePart, ampm] = time.split(' ');
        const [h, m] = timePart.split(':').map(Number);
        
        hours = h;
        if (ampm === 'PM' && hours !== 12) {
          hours += 12;
        } else if (ampm === 'AM' && hours === 12) {
          hours = 0;
        }
        minutes = m || 0;
      } else {
        const [h, m] = time.split(':').map(Number);
        hours = h || 0;
        minutes = m || 0;
      }
      
      // Ensure hours are within 0-23 range
      hours = Math.max(0, Math.min(23, hours));
      minutes = Math.max(0, Math.min(59, minutes));
      
      // Calculate total milliseconds and convert to ticks
      // 1 tick = 100 nanoseconds = 0.0001 milliseconds
      const totalMilliseconds = (hours * 3600 + minutes * 60) * 1000;
      const ticks = totalMilliseconds * 10000; // Convert ms to ticks
      
      return Math.round(ticks);
    } catch (error) {
      console.error('Error converting time to ticks:', error);
      return 324000000000; // Default to 9:00 AM if conversion fails
    }
  };

  const validateForm = () => {
    if (!notificationData.eventType) {
      toast.error('Please select an Event Type');
      return false;
    }

    if (!notificationData.notificationTiming) {
      toast.error('Please select Schedule Timing');
      return false;
    }

    const selectedChannels = Object.values(notificationData.channels).filter(v => v);
    if (selectedChannels.length === 0) {
      toast.error('Please select at least one Delivery Channel');
      return false;
    }

    const selectedTemplates = Object.values(notificationData.templates).filter(t => t.templateId);
    if (selectedTemplates.length === 0) {
      toast.error('Please select at least one Template for the selected channels');
      return false;
    }

    const selectedRecipients = Object.values(notificationData.recipients).filter(v => v);
    const validEmails = notificationData.directEmails.filter(email => email.trim() !== '');
    
    if (selectedRecipients.length === 0 && validEmails.length === 0) {
      toast.error('Please select at least one recipient type or provide direct email addresses');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      const storedUser = getStoredUser();
      let createdBy = 'system';
      let organisationId = 0;
      
      if (storedUser) {
        try {
          if (storedUser.userDetails) {
            const userDetails = JSON.parse(storedUser.userDetails);
            createdBy = userDetails.userId ? String(userDetails.userId) : 'system';
            organisationId = userDetails.organisationId || storedUser.organisationId || 0;
          } else {
            createdBy = storedUser.userId ? String(storedUser.userId) : 'system';
            organisationId = storedUser.organisationId || 0;
          }
        } catch (e) {
          console.error('Error parsing user details:', e);
          createdBy = storedUser.userId ? String(storedUser.userId) : 'system';
          organisationId = storedUser.organisationId || 0;
        }
      }

      // Get delivery IDs
      const validDeliveryIds = notificationData.deliveryIds
        .filter(id => id > 0)
        .map(id => id.toString());
      
      if (validDeliveryIds.length === 0) {
        toast.error('No valid delivery channels selected. Please select at least one delivery channel.');
        setSubmitting(false);
        return;
      }

      const deliveryIdsString = validDeliveryIds.join(',');
      
      // Get template IDs
      const selectedTemplateIds = Object.values(notificationData.templates)
        .filter(t => t.templateId && t.templateId > 0)
        .map(t => t.templateId.toString());
      
      if (selectedTemplateIds.length === 0) {
        toast.error('No valid templates selected. Please select at least one template.');
        setSubmitting(false);
        return;
      }
      
      const templateIdsString = selectedTemplateIds.join(',');

      // Get recipient IDs
      const selectedRecipientIds = Object.keys(notificationData.recipients)
        .filter(id => notificationData.recipients[id])
        .map(id => id);
      
      if (selectedRecipientIds.length === 0) {
        // Check if we have direct emails
        const validEmails = notificationData.directEmails.filter(email => email.trim() !== '');
        if (validEmails.length === 0) {
          toast.error('No recipients selected. Please select at least one recipient or provide email addresses.');
          setSubmitting(false);
          return;
        }
        // Use a default recipient ID if only emails are provided
        selectedRecipientIds.push("1"); // Default to Self
      }
      
      const recipientIdsString = selectedRecipientIds.join(',');

      // Get office codes
      const officeLocationString = notificationData.offices.length > 0 
        ? notificationData.offices.join(',') 
        : "0";

      // Get valid emails
      const validEmails = notificationData.directEmails
        .filter(email => email.trim() !== '')
        .map(email => email.trim());

      // Prepare event date
      let eventDateISO = null;
      if (notificationData.customDate) {
        eventDateISO = notificationData.customDate.toISOString();
      }

      // Convert preferTime to ticks
      const preferTimeTicks = notificationData.preferTime ? timeToTicks(notificationData.preferTime) : 0;

      // Build the submissionData object - NO templateVar needed
      const submissionData = {
        eventId: notificationData.eventId || 0,
        scheduleId: notificationData.scheduleId || 0,
        templateId: templateIdsString,
        deliveryId: deliveryIdsString,
        recipientId: recipientIdsString,
        officeLocation: officeLocationString,
        eventDate: eventDateISO,
        notificationStatus: "scheduled",
        createdBy: createdBy,
        emailArr: validEmails.length > 0 ? validEmails : [],
        preferTime: preferTimeTicks,
        organisationId: organisationId
      };

      console.log('📤 FINAL PAYLOAD:', JSON.stringify(submissionData, null, 2));
      console.log('📋 ID Storage Details:');
      console.log('- Template IDs:', submissionData.templateId);
      console.log('- Delivery IDs:', submissionData.deliveryId);
      console.log('- Recipient IDs:', submissionData.recipientId);

      const result = await postNotificationData(submissionData);
      
      toast.success('✅ Notification scheduled successfully!');
      
      // Show storage details
      if (result.StoredData) {
        console.log('💾 Storage Details:');
        console.log('- TemplateId (int):', result.StoredData.TemplateId);
        console.log('- TemplateIds (string):', result.StoredData.TemplateIds);
        console.log('- DeliveryId (int):', result.StoredData.DeliveryId);
        console.log('- DeliveryIds (string):', result.StoredData.DeliveryIds);
        console.log('- RecipientId (int):', result.StoredData.RecipientId);
        console.log('- RecipientIds (string):', result.StoredData.RecipientIds);
      }
      
      handleReset();
      
    } catch (error) {
      console.error('❌ Error submitting notification:', error);
      
      if (error.response && error.response.data) {
        const errorData = error.response.data;
        if (errorData.message && errorData.message.includes("Foreign key constraint violation")) {
          toast.error('❌ Invalid IDs provided. Please check that all selected IDs exist in the system.');
        } else if (errorData.errors && Array.isArray(errorData.errors)) {
          errorData.errors.forEach(err => toast.error(`❌ ${err}`));
        } else if (errorData.Message) {
          toast.error(`❌ ${errorData.Message}`);
        } else {
          toast.error(`❌ Error: ${errorData.message || 'Failed to schedule notification'}`);
        }
      } else {
        toast.error('❌ Failed to schedule notification. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    const initialChannels = {};
    const initialRecipients = {};
    const initialTemplates = {};
    
    getChannelOptions().forEach(channel => {
      initialChannels[channel.id] = false;
    });
    
    recipientCategories.forEach(recipient => {
      initialRecipients[recipient.id] = false;
    });
    
    getChannelOptions().forEach(channel => {
      initialTemplates[channel.id] = { 
        templateId: null, 
        templateTittle: '', 
        templateDescription: '', 
        templateVariables: '' 
      };
    });
    
    setNotificationData({
      eventType: '',
      eventId: null,
      notificationTiming: '',
      scheduleId: null,
      channels: initialChannels,
      deliveryIds: [],
      offices: [],
      recipients: initialRecipients,
      directEmails: [''],
      templates: initialTemplates,
      templateIds: [],
      customDate: null,
      preferTime: '',
    });
    setShowDatePicker(false);
  };

  const selectedEvent = apiData.eventTypes?.find(e => e.eventType === notificationData.eventType);
  const timingOptions = getTimingOptions();
  const channelOptions = getChannelOptions();

  if (loading) {
    return (
      <Container className="org-notifications-loading-container">
        <div className="org-notifications-loading-spinner"></div>
        <div className="org-notifications-loading-text">Loading notification data...</div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="org-notifications-error-container">
        <AlertCircle size={64} className="org-notifications-error-icon" />
        <h2 className="org-notifications-error-title">Error Loading Data</h2>
        <p className="org-notifications-error-message">{error}</p>
        <Button 
          variant="primary" 
          onClick={fetchNotificationData}
          className="org-notifications-retry-btn"
        >
          <RefreshCw size={18} className="me-2" />
          Retry
        </Button>
      </Container>
    );
  }

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        style={{ zIndex: 9999 }}
      />

      <Container className="org-notifications-main">
        {/* Header Section */}
        <div className="org-notifications-header">
          <div className="org-notifications-header-content">
            <div className="org-notifications-icon-wrapper">
              <Bell size={42} />
            </div>
            <div className="org-notifications-title-section">
              <h1 className="org-notifications-main-title">Notification Generator</h1>
              <p className="org-notifications-subtitle">
                Create personalized notifications across multiple channels with custom templates
              </p>
            </div>
            <Badge className="org-notifications-schedule-badge">
              <Clock size={16} className="me-2" />
              Real-time Scheduling
            </Badge>
          </div>
        </div>

        <Form onSubmit={handleSubmit} className="org-notifications-form">
          {/* Event Selection Section */}
          <div className="org-notifications-section-card">
            <div className="org-notifications-section-header">
              <div className="org-notifications-section-icon-wrapper">
                <Calendar size={24} />
              </div>
              <div className="org-notifications-section-title-content">
                <h2 className="org-notifications-section-title">1. Select Event Type</h2>
                <p className="org-notifications-section-description">Choose the type of event for notification</p>
              </div>
            </div>
            <div className="org-notifications-section-body">
              {apiData.eventTypes && apiData.eventTypes.length > 0 ? (
                <>
                  <div className="org-notifications-event-grid">
                    {apiData.eventTypes.map(event => (
                      <div
                        key={event.eventId}
                        className={`org-notifications-event-option ${notificationData.eventType === event.eventType ? 'org-notifications-option-selected' : ''}`}
                        onClick={() => handleEventTypeChange(event.eventId, event.eventType)}
                        style={{ borderLeftColor: eventColors[event.eventType] || '#ccc' }}
                      >
                        <div className="org-notifications-event-icon-container">
                          {iconMapping[event.eventIcon] || <Calendar size={20} />}
                        </div>
                        <span className="org-notifications-event-label">{event.eventType}</span>
                        {notificationData.eventType === event.eventType && (
                          <CheckCircle size={20} className="org-notifications-selected-indicator" />
                        )}
                      </div>
                    ))}
                  </div>
                  {selectedEvent && (
                    <Alert variant="light" className="org-notifications-selected-alert">
                      <strong>Selected Event:</strong> {selectedEvent.eventType}
                      {selectedEvent.eventDescription && (
                        <span className="ms-2">- {selectedEvent.eventDescription}</span>
                      )}
                    </Alert>
                  )}
                </>
              ) : (
                <Alert variant="warning" className="text-center">
                  No event types available. Please configure event types in the system.
                </Alert>
              )}
            </div>
          </div>

          {/* Timing Section */}
          <div className="org-notifications-section-card">
            <div className="org-notifications-section-header">
              <div className="org-notifications-section-icon-wrapper">
                <Clock size={24} />
              </div>
              <div className="org-notifications-section-title-content">
                <h2 className="org-notifications-section-title">2. Schedule Timing</h2>
                <p className="org-notifications-section-description">When should notifications be sent?</p>
              </div>
            </div>
            <div className="org-notifications-section-body">
              {timingOptions.length > 0 ? (
                <Row className="g-4">
                  {timingOptions.map(timing => (
                    <Col md={6} lg={4} key={timing.id}>
                      <div
                        className={`org-notifications-timing-option ${notificationData.notificationTiming === timing.label ? 'org-notifications-option-selected' : ''}`}
                        onClick={() => handleTimingSelect(timing)}
                      >
                        <div className="org-notifications-timing-icon">{timing.icon}</div>
                        <div className="org-notifications-timing-details">
                          <h5 className="org-notifications-timing-title">{timing.label}</h5>
                          <p className="org-notifications-timing-description">{timing.description}</p>
                          {timing.label === 'Custom Date' && notificationData.customDate && (
                            <div className="org-notifications-custom-date-display">
                              <small>Selected: {notificationData.customDate.toLocaleDateString()}</small>
                            </div>
                          )}
                        </div>
                        {notificationData.notificationTiming === timing.label && (
                          <CheckCircle size={20} className="org-notifications-selection-check" />
                        )}
                      </div>
                    </Col>
                  ))}
                </Row>
              ) : (
                <Alert variant="warning" className="text-center">
                  No schedule timings available. Please configure schedule timings in the system.
                </Alert>
              )}
            </div>
          </div>

          {/* Prefer Time Section */}
          <div className="org-notifications-section-card">
            <div className="org-notifications-section-header">
              <div className="org-notifications-section-icon-wrapper">
                <Clock4 size={24} />
              </div>
              <div className="org-notifications-section-title-content">
                <h2 className="org-notifications-section-title">3. Preferred Time</h2>
                <p className="org-notifications-section-description">Select preferred time for notification delivery</p>
              </div>
            </div>
            <div className="org-notifications-section-body">
              <Row>
                <Col md={6}>
                  <Form.Group controlId="preferTime">
                    <Form.Label className="org-notifications-form-label">Preferred Time <small className="text-muted">(e.g., 2:30 PM or 14:30)</small></Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Enter time (e.g., 2:30 PM or 14:30)"
                      value={notificationData.preferTime}
                      onChange={(e) => handlePreferTimeChange(e.target.value)}
                      className="org-notifications-time-input"
                    />
                    <Form.Text className="text-muted org-notifications-form-text">
                      Enter time in 12-hour (2:30 PM) or 24-hour (14:30) format
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <div className="org-notifications-time-examples">
                    <h6>Examples:</h6>
                    <ul className="mb-0">
                      <li><code>9:00 AM</code> - Morning</li>
                      <li><code>2:30 PM</code> - Afternoon</li>
                      <li><code>18:00</code> - Evening (6:00 PM)</li>
                      <li><code>10:00</code> - Morning (10:00 AM)</li>
                    </ul>
                  </div>
                </Col>
              </Row>
              {notificationData.preferTime && (
                <Alert variant="info" className="org-notifications-selected-alert mt-3">
                  <strong>Selected Time:</strong> {notificationData.preferTime}
                  {notificationData.preferTime && (
                    <span className="ms-2">
                      <strong>Ticks Value:</strong> {timeToTicks(notificationData.preferTime).toLocaleString()}
                    </span>
                  )}
                </Alert>
              )}
            </div>
          </div>

          {/* Channel Selection */}
          <div className="org-notifications-section-card">
            <div className="org-notifications-section-header">
              <div className="org-notifications-section-icon-wrapper">
                <Send size={24} />
              </div>
              <div className="org-notifications-section-title-content">
                <h2 className="org-notifications-section-title">4. Delivery Channels</h2>
                <p className="org-notifications-section-description">Select platforms for notification delivery</p>
              </div>
            </div>
            <div className="org-notifications-section-body">
              {channelOptions.length > 0 ? (
                <div className="org-notifications-channel-grid">
                  {channelOptions.map(channel => (
                    <div
                      key={channel.id}
                      className={`org-notifications-channel-option ${notificationData.channels[channel.id] ? 'org-notifications-option-selected' : ''}`}
                      onClick={() => handleChannelToggle(channel)}
                    >
                      <div className="org-notifications-channel-icon-container">
                        {channel.icon}
                      </div>
                      <span className="org-notifications-channel-label">{channel.label}</span>
                      <div className="org-notifications-channel-checkbox">
                        <input
                          type="checkbox"
                          checked={notificationData.channels[channel.id]}
                          onChange={() => {}}
                          readOnly
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <Alert variant="warning" className="text-center">
                  No delivery channels available. Please configure delivery channels in the system.
                </Alert>
              )}
            </div>
          </div>

          {/* Recipients Section */}
          <div className="org-notifications-section-card">
            <div className="org-notifications-section-header">
              <div className="org-notifications-section-icon-wrapper">
                <Users size={24} />
              </div>
              <div className="org-notifications-section-title-content">
                <h2 className="org-notifications-section-title">5. Select Recipients</h2>
                <p className="org-notifications-section-description">Choose who will receive these notifications</p>
              </div>
            </div>
            <div className="org-notifications-section-body">
              {/* Offices */}
              <div className="org-notifications-recipients-section">
                <h5 className="org-notifications-recipients-subtitle">
                  <Building size={20} className="me-2" />
                  Office Locations
                </h5>
                {apiData.officeMasters && apiData.officeMasters.length > 0 ? (
                  <div className="org-notifications-office-grid">
                    {apiData.officeMasters.map(office => {
                      const colors = ['#D4A5A5', '#9DC9AC', '#FFCBA4', '#AEC6CF', '#FDFD96'];
                      const colorIndex = office.officeCode % colors.length;
                      
                      return (
                        <div
                          key={office.officeCode}
                          className={`org-notifications-office-option ${notificationData.offices.includes(office.officeCode) ? 'org-notifications-option-selected' : ''}`}
                          onClick={() => handleOfficeToggle(office.officeCode)}
                          style={{ borderLeftColor: colors[colorIndex] }}
                        >
                          <div className="org-notifications-office-info">
                            <h6 className="org-notifications-office-name">{office.officeName}</h6>
                            <small className="org-notifications-office-count">
                              Code: {office.officeCode}
                            </small>
                          </div>
                          {notificationData.offices.includes(office.officeCode) && (
                            <Badge className="org-notifications-office-badge">Selected</Badge>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <Alert variant="info" className="text-center">
                    No office locations configured.
                  </Alert>
                )}
              </div>

              {/* Recipient Categories */}
              <div className="org-notifications-recipients-section mt-4">
                <h5 className="org-notifications-recipients-subtitle">Recipient Categories</h5>
                {recipientCategories.length > 0 ? (
                  <div className="org-notifications-recipient-categories">
                    {recipientCategories.map(recipient => (
                      <div
                        key={recipient.id}
                        className={`org-notifications-recipient-category ${notificationData.recipients[recipient.id] ? 'org-notifications-option-selected' : ''}`}
                        onClick={() => handleRecipientToggle(recipient.id)}
                      >
                        <div className="org-notifications-category-checkbox">
                          <input
                            type="checkbox"
                            checked={notificationData.recipients[recipient.id] || false}
                            onChange={() => {}}
                            readOnly
                          />
                        </div>
                        <span className="org-notifications-category-label">
                          {recipient.label}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <Alert variant="info" className="text-center">
                    No recipient categories available.
                  </Alert>
                )}
              </div>

              {/* Direct Emails */}
              <div className="org-notifications-recipients-section mt-4">
                <h5 className="org-notifications-recipients-subtitle">Direct Email Addresses</h5>
                <div className="org-notifications-email-container">
                  {notificationData.directEmails.map((email, index) => (
                    <div key={index} className="org-notifications-email-row">
                      <FloatingLabel 
                        controlId={`email-${index}`} 
                        label={`Email Address ${index + 1}`}
                        className="org-notifications-email-input-wrapper"
                      >
                        <Form.Control
                          type="email"
                          placeholder="recipient@company.com"
                          value={email}
                          onChange={(e) => handleDirectEmailChange(index, e.target.value)}
                          className="org-notifications-email-input"
                        />
                      </FloatingLabel>
                      <div className="org-notifications-email-actions">
                        {notificationData.directEmails.length > 1 && (
                          <Button
                            variant="outline-danger"
                            size="sm"
                            onClick={() => handleRemoveEmail(index)}
                            className="org-notifications-remove-email-btn"
                          >
                            ×
                          </Button>
                        )}
                        {index === notificationData.directEmails.length - 1 && (
                          <Button
                            variant="outline-success"
                            size="sm"
                            onClick={() => {
                              const newEmails = [...notificationData.directEmails, ''];
                              setNotificationData(prev => ({ ...prev, directEmails: newEmails }));
                            }}
                            className="org-notifications-add-email-btn"
                          >
                            +
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Templates Section - SIMPLIFIED */}
          <div className="org-notifications-section-card">
            <div className="org-notifications-section-header">
              <div className="org-notifications-section-icon-wrapper">
                <LayoutTemplate size={24} />
              </div>
              <div className="org-notifications-section-title-content">
                <h2 className="org-notifications-section-title">6. Message Templates</h2>
                <p className="org-notifications-section-description">Select templates for each channel</p>
              </div>
            </div>
            <div className="org-notifications-section-body">
              {Object.keys(notificationData.channels).filter(ch => notificationData.channels[ch]).length > 0 ? (
                <Accordion className="org-notifications-templates-accordion">
                  {channelOptions
                    .filter(ch => notificationData.channels[ch.id])
                    .map((channel, index) => {
                      const templates = notificationData.eventType 
                        ? getTemplatesByEventAndChannel(notificationData.eventType, channel.id)
                        : [];
                      
                      return (
                        <Accordion.Item eventKey={index.toString()} key={channel.id}>
                          <Accordion.Header className="org-notifications-accordion-header">
                            <div className="d-flex align-items-center w-100">
                              <div className="org-notifications-channel-header-icon me-3">
                                {channel.icon}
                              </div>
                              <div className="flex-grow-1">
                                <h5 className="mb-0 org-notifications-accordion-title">{channel.label} Templates</h5>
                                <small className="org-notifications-accordion-subtitle">
                                  {notificationData.templates[channel.id]?.templateId 
                                    ? `${notificationData.templates[channel.id].templateTittle} selected`
                                    : 'No template selected'}
                                </small>
                              </div>
                              {notificationData.templates[channel.id]?.templateId && (
                                <Badge className="org-notifications-template-badge">✓ Selected</Badge>
                              )}
                            </div>
                          </Accordion.Header>
                          <Accordion.Body className="org-notifications-accordion-body">
                            {templates.length > 0 ? (
                              <div className="org-notifications-template-list">
                                {templates.map(template => (
                                  <div
                                    key={template.templateId}
                                    className={`org-notifications-template-item ${notificationData.templates[channel.id]?.templateId === template.templateId ? 'org-notifications-template-selected' : ''}`}
                                    onClick={() => handleTemplateSelect(channel.id, template)}
                                  >
                                    <div className="org-notifications-template-item-header">
                                      <h6 className="org-notifications-template-name">{template.templateTittle}</h6>
                                      <Badge className="org-notifications-template-id-badge">
                                        ID: {template.templateId}
                                      </Badge>
                                    </div>
                                    <div className="org-notifications-template-preview">
                                      <div className="org-notifications-template-field">
                                        <small className="org-notifications-template-label">Template Type:</small>
                                        <p className="org-notifications-template-content">{template.templateType}</p>
                                      </div>
                                      <div className="org-notifications-template-field">
                                        <small className="org-notifications-template-label">Description:</small>
                                        <p className="org-notifications-template-preview-text">{template.templateDescription}</p>
                                      </div>
                                    </div>
                                    {notificationData.templates[channel.id]?.templateId === template.templateId && (
                                      <div className="org-notifications-template-selected-indicator">
                                        <CheckCircle size={16} /> Selected
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <Alert variant="info" className="org-notifications-no-templates-alert">
                                {notificationData.eventType 
                                  ? `No ${channel.label} templates available for ${notificationData.eventType}.`
                                  : 'Please select an event type first.'}
                              </Alert>
                            )}
                          </Accordion.Body>
                        </Accordion.Item>
                      );
                    })}
                </Accordion>
              ) : (
                <Alert variant="warning" className="org-notifications-no-channels-alert">
                  Select at least one delivery channel to configure templates.
                </Alert>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="org-notifications-action-buttons">
            <Button
              variant="outline-secondary"
              size="lg"
              onClick={handleReset}
              className="org-notifications-reset-btn"
              disabled={submitting}
            >
              Reset All
            </Button>
            <div className="d-flex gap-3">
              <Button
                type="submit"
                variant="primary"
                size="lg"
                className="org-notifications-submit-btn"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    Scheduling...
                  </>
                ) : (
                  <>
                    <Send size={18} className="me-2" />
                    Schedule Notification
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Stats Footer */}
          <Card className="org-notifications-stats-card">
            <Card.Body className="org-notifications-stats-body">
              <Row className="text-center">
                <Col md={3} className="org-notifications-stat-column">
                  <div className="org-notifications-stat-value">{notificationData.offices.length}</div>
                  <div className="org-notifications-stat-label">Offices</div>
                </Col>
                <Col md={3} className="org-notifications-stat-column">
                  <div className="org-notifications-stat-value">
                    {Object.values(notificationData.channels).filter(v => v).length}
                  </div>
                  <div className="org-notifications-stat-label">Channels</div>
                </Col>
                <Col md={3} className="org-notifications-stat-column">
                  <div className="org-notifications-stat-value">
                    {Object.values(notificationData.recipients).filter(v => v).length}
                  </div>
                  <div className="org-notifications-stat-label">Recipient Types</div>
                </Col>
                <Col md={3} className="org-notifications-stat-column">
                  <div className="org-notifications-stat-value">
                    {Object.values(notificationData.templates).filter(t => t.templateId).length}
                  </div>
                  <div className="org-notifications-stat-label">Templates</div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Form>
      </Container>

      {/* Custom Date Picker Modal */}
      <Modal
        show={showDatePicker}
        onHide={() => setShowDatePicker(false)}
        size="sm"
        centered
        className="org-notifications-date-modal"
      >
        <Modal.Header closeButton className="org-notifications-modal-header">
          <Modal.Title className="org-notifications-modal-title">
            <Calendar size={20} className="me-2" />
            Select Custom Date
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="org-notifications-modal-body text-center">
          <div className="mb-4">
            <Form.Group controlId="customDate">
              <Form.Label>Select a date</Form.Label>
              <Form.Control
                type="date"
                min={new Date().toISOString().split('T')[0]}
                value={notificationData.customDate ? notificationData.customDate.toISOString().split('T')[0] : ''}
                onChange={(e) => {
                  const date = e.target.value ? new Date(e.target.value) : null;
                  setNotificationData(prev => ({
                    ...prev,
                    customDate: date
                  }));
                }}
                className="org-notifications-date-input"
              />
            </Form.Group>
          </div>
          {notificationData.customDate && (
            <Alert variant="info">
              Selected: <strong>{notificationData.customDate.toLocaleDateString()}</strong>
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer className="org-notifications-modal-footer">
          <Button variant="secondary" onClick={() => setShowDatePicker(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={() => {
              if (notificationData.customDate) {
                handleCustomDateSelect(notificationData.customDate);
              } else {
                toast.error('Please select a date');
              }
            }}
          >
            Confirm Date
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default OrgNotifications;